import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { AccountService } from 'app/core';
import { Dictionary } from 'app/shared/models/dictionary';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { AddDispositionsGroupComponent } from '../../@shared/components/add-dispositions-group/add-dispositions-group.component';
import { AddDispositionsComponent } from '../../@shared/components/add-dispositions/add-dispositions.component';
import { DispositionsService } from '../../@shared/services/dispositions.service';
import { Disposition, DispositionGroup } from '../../@shared/types/dispositions.types';
import { ContactsService } from 'app/modules/contacts/contacts.service';
import { LayoutService } from 'app/layout/layout.service';
import { Project } from '../../projects.types';
import { FilesCommentsFormComponent } from 'app/shared/modules/files-comments-form/files-comments-form/files-comments-form.component';
import { RelatedToType } from 'app/shared/enums';
import { Store } from '@ngxs/store';
import { NewPermission } from '@core/enums';

@Component({
  selector: 'app-dispositions-index',
  templateUrl: './index.component.html',
  styleUrls: ['./index.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DispositionsIndexComponent extends OnDestroyMixin implements OnInit {
  dispositions$: Observable<DispositionGroup[]>;
  selectedDispositionGroup: DispositionGroup;
  ui = { isLoading: true };
  NewPermission = NewPermission;

  private projectId: number;
  private dispositionGroupId: number;
  private data: any;
  private shouldRefresh = false;

  get options(): Dictionary<any[]> {
    return this.data.options;
  }

  get project(): Project {
    return this.data.project;
  }

  private get accountId(): string {
    return this.accountService.getCurrentAccount()?.id;
  }

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly matDialog: MatDialog,
    private readonly layoutService: LayoutService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly accountService: AccountService,
    private readonly contactService: ContactsService,
    private readonly dispositionsService: DispositionsService,
    private readonly store: Store,
  ) {
    super();
  }

  ngOnInit(): void {
    this.activatedRoute.parent.parent.data.subscribe((data: Data) => (this.data = data)).unsubscribe();
    this.activatedRoute.params.subscribe(({ id }: { id: number }) => (this.dispositionGroupId = +id)).unsubscribe();
    this.loadDispositions();
    this.layoutService.close$.pipe(untilComponentDestroyed(this)).subscribe(() => {
      if (this.shouldRefresh) {
        this.shouldRefresh = false;
        this.refresh();
      }
    });
  }

  select(item: DispositionGroup): void {
    this.selectedDispositionGroup = item;
    if (item) {
      this.dispositionGroupId = item.id;
      this.router.navigate(['./', item.id], { relativeTo: this.activatedRoute });
    }
  }

  deleteGroup(): void {
    this.dispositionsService.deleteDispositionGroup(this.selectedDispositionGroup.id).subscribe(() => this.refresh());
  }

  openModal(type: 'AddDispositionGroup' | 'EditDispositionGroup' | 'AddDisposition'): void {
    let componentClass: any;
    const data = { project: this.project, options: this.options, group: this.selectedDispositionGroup };

    switch (type) {
      case 'AddDispositionGroup':
        componentClass = AddDispositionsGroupComponent;
        delete data.group;
        break;
      case 'EditDispositionGroup':
        componentClass = AddDispositionsGroupComponent;
        break;
      case 'AddDisposition':
        componentClass = AddDispositionsComponent;
        break;
    }

    const dialogRef = this.matDialog.open(componentClass, {
      panelClass: 'mailbox-compose-dialog',
      data,
    });

    dialogRef
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe((result: DispositionGroup | Disposition) => {
        if (type === 'AddDispositionGroup') {
          this.dispositionGroupId = result.id;
        }

        this.refresh();
      });
  }

  onOpenFilesAndCommentsDrawer(disposition: Disposition): void {
    this.layoutService.openComponentDrawer(FilesCommentsFormComponent, {
      inputs: {
        data: {
          project: this.project,
        },
        commentsConfig: {
          relatedToId: disposition.id,
          relatedToType: RelatedToType.DispositionGroupTransaction,
        },
        fileResources: disposition.file_resources,
      },
      outputs: {
        onFileUpdated: () => (this.shouldRefresh = true),
      },
    });
  }

  private refresh(): void {
    this.ui = { isLoading: true };
    this.router.navigate(['./'], { relativeTo: this.activatedRoute });
    this.loadDispositions();
    this.changeDetectorRef.detectChanges();
  }

  private loadDispositions(): void {
    this.dispositions$ = this.activatedRoute.parent.parent.params.pipe(
      untilComponentDestroyed(this),
      switchMap(({ project_id }: { project_id: number }) => {
        this.projectId = project_id ?? +this.dispositionsService.projectId;

        this.dispositionsService.accountId = this.accountId;
        this.dispositionsService.projectId = String(this.projectId);
        this.contactService.accountId = this.accountId;

        return this.dispositionsService.getDispositionGroups();
      }),
      map(({ disposition_groups }) => disposition_groups),
      tap((dispositionGroups) => {
        if (this.dispositionGroupId) {
          this.select(dispositionGroups?.find(({ id }) => id === this.dispositionGroupId) || dispositionGroups[0]);
        } else if (dispositionGroups?.length) {
          this.select(dispositionGroups[0]);
        }

        this.ui = { isLoading: false };
      }),
    );
  }

  onDispositionEditedUpdated({ disposition, deleted }: { disposition: Disposition; deleted?: boolean }): void {
    if (disposition || deleted) {
      this.refresh();
    }
  }
}
